import { QC_PRODUCT_STATE } from '@/helpers/states';
import { QC_DECISION_STATE_TYPE, QC_STATE_TYPE } from '@/types/QcStatus';

import { AddItemStatusType, AdditionalItemType } from './AdditionalItem';
import { AgentInfoType } from './Agent';
import { CheckpointStatueType, CheckPointType } from './Checkpoint';
import {
  ActualMeasurement,
  DefectRequest,
  SubmittedDefect,
} from './interfaces/defect-details';
import { IHydraBase } from './interfaces/hydra';
import { ProductType } from './ProductInfo';
import { StandbyReasonType } from './StandbyReasons';

export interface QcInfoRecordType {
  additionalItemRecords: {
    additionalItem: AdditionalItemType;
    status: AddItemStatusType;
  }[];
  agentComment: string | null;
  agentPublicComment: string | null;
  checkpointRecords: {
    id: number;
    status: CheckpointStatueType;
    checkpoint: CheckPointType;
    defectRecords: SubmittedDefect[];
  }[];
  hallmarks: string | null;
  isProhibited: boolean;
  isRefurbished: boolean;
  isFunctioned: boolean;
  forceSellerReturn: boolean;
  weight: number | null;
  photos: DefectRequest['photos'];
  internalAdditionalPhotoUrls: string[];
  internalAdditionalPhotos: string[];
  publicAdditionalPhotoUrls: string[];
  publicAdditionalPhotos: string[];
  actualCoo?: string;
  actualCooId?: string;
  cooConfirmation?: boolean;
  actualMeasurements?: ActualMeasurement[] | null;
}
export interface QcInfoType extends IHydraBase {
  agent: AgentInfoType | null;
  createdAt: string;
  lastAgentActionDate: Date | null;
  product: ProductType;
  state: (typeof QC_PRODUCT_STATE)[keyof typeof QC_PRODUCT_STATE];
  stateName: string;
  stateUpdatedAt?: string;
  updatedAt: string;
  uuid: string;
  agentStartedAt: Date | null;
  standbyReason: StandbyReasonType;
  standbyComment: string | null;
  record: QcInfoRecordType;
  forceSellerReturn: boolean;
  forceSellerReturnEditable: boolean;
  // submitted is a local state to identify if the item qc is submitted to the backend
  submitted?: boolean;
  qualityControlRound?: number;
  needSecondQualityControl?: boolean;
}

export interface QcRecordType {
  additionalItemRecords?:
    | {
        additionalItemUuid: string;
        status: AddItemStatusType;
      }[]
    | null;
  checkpointRecords?:
    | {
        checkpointId?: number;
        status: CheckpointStatueType;
        defectRecordIds?: number[];
      }[]
    | null;
  agentComment?: string | null;
  weight?: number | null;
  hallmarks?: string | null;
  isRefurbished?: boolean;
  actualMeasurements?: ActualMeasurement[] | null;
}

export interface QcListQueryParams {
  page: number;
  'product.id': number | null;
  'agent.email': string | string[] | null;
  state: QC_STATE_TYPE[];
  lastAgentActionDateFrom: Date | null;
  lastAgentActionDateTo: Date | null;
  'order[lastAgentActionDate]': 'asc' | 'desc';
  'product.warehouse.id': number | null;
  'standbyReason.id': number[] | null;
  itemsPerPage: number;
}

export interface QcListType {
  data: QcInfoType[];
  totalItems: number;
}

export type UpdateQcState = {
  uuid: string;
  state: QC_DECISION_STATE_TYPE;
  standbyReasonUuid?: string | null;
  record?: QcRecordType | null;
  standbyComment?: string | null;
};

export enum ErrorField {
  WEIGHT = 'weight',
  HALLMARKS = 'hallmarks',
  WATCH = 'watch',
  ORIGIN_COUNTRY = 'originCountry',
}

export type ErrorType = {
  field: ErrorField;
  message: string;
};

export type Country = {
  legacyId: string;
  name: string;
  isoCode: string;
};

export type DefaultComment = {
  name: string;
  code: string;
};

export type AdditionalInfo = {
  refurbishment: boolean;
  forceSellerReturn: boolean;
  isWorkingWatch?: boolean;
};
